/**
 * Military Panel Component
 * 
 * Displays military unit information, production rates, and troop planning.
 * Ported from the Military tab in main.mxml
 */

import { UNITS } from '../core/UnitData.js';

export class MilitaryPanel {
    constructor(containerElement, gameEngine) {
        this.container = containerElement;
        this.gameEngine = gameEngine;
        this.selectedUnits = new Map(); // Map of unit name -> count
        this._incrementButtonHandler = null; // Store handler reference for cleanup
        
        this.render();
    }
    
    /**
     * Render the military panel
     */
    render() {
        if (!this.gameEngine) return;
        
        const { gameEngine } = this;
        
        // Calculate production rates
        const rates = {
            guard: gameEngine.guard_rate,
            training_ground: gameEngine.training_ground_rate,
            stable: gameEngine.stable_rate,
            moonglow: gameEngine.moonglow_rate,
            workshop: gameEngine.workshop_rate,
            shipyard: gameEngine.shipyard_rate,
            temple: gameEngine.temple_rate
        };
        
        // Define rate cards with their data
        const rateCards = [
            { label: 'Guard House', count: gameEngine.guard_tot, rate: rates.guard },
            { label: 'Training Area', count: gameEngine.training_ground_tot, rate: rates.training_ground },
            { label: 'Stable', count: gameEngine.stable_tot, rate: rates.stable },
            { label: "Sorcerer's Tower", count: gameEngine.moonglow_tot, rate: rates.moonglow },
            { label: 'Blacksmith', count: gameEngine.workshop_tot, rate: rates.workshop },
            { label: 'Shipyard', count: gameEngine.shipyard_tot, rate: rates.shipyard },
            { label: 'Academy', count: gameEngine.temple_tot, rate: rates.temple }
        ];
        
        // Filter to only show rate cards with rate > 0
        const activeRateCards = rateCards.filter(card => card.rate > 0);
        
        // Build production rates grid HTML
        const productionRatesGrid = activeRateCards.length > 0
            ? activeRateCards.map(card => `
                <div class="rate-card">
                    <div class="rate-label">${card.label}</div>
                    <div class="rate-count">x${this.formatNumber(card.count)}</div>
                    <div class="rate-value">${Math.round(card.rate * 100)}%</div>
                </div>
            `).join('')
            : '<div class="no-rates-message" style="grid-column: 1 / -1; text-align: center; padding: 20px; color: #aaa; font-style: italic;">No active production rates. Add military buildings to enable unit production.</div>';
        
        // Build unit rows - only show units that can be made (have a build rate > 0)
        const availableUnits = UNITS.filter(unit => {
            const buildRate = unit.getBuildRate(gameEngine);
            return buildRate > 0;
        });
        
        // Sort units by production rate (highest first)
        availableUnits.sort((a, b) => {
            const rateA = a.getBuildRate(gameEngine);
            const rateB = b.getBuildRate(gameEngine);
            return rateB - rateA; // Descending order (highest first)
        });
        
        const unitRows = availableUnits.length > 0 
            ? availableUnits.map(unit => {
                const buildRate = unit.getBuildRate(gameEngine);
                const buildRatePercent = Math.round(buildRate * 100);
                // Calculate build time in seconds: productionSpeed / buildRate
                // buildRate is a multiplier (e.g., 2.5 = 250%), productionSpeed is base time
                const buildTimeSeconds = buildRate > 0 ? unit.productionSpeed / buildRate : 0;
                // Format individual build time (use mm:ss for times >= 1 minute)
                const buildTimeFormatted = this.formatBuildTime(buildTimeSeconds);
                
                // Calculate max units that can fit in capacity
                // Note: Units with space = 0 (like Guard) don't take capacity, so max is effectively unlimited
                // This is the initial display value - updateSliderMaxValues() will recalculate based on selected units
                const maxUnits = unit.space > 0 ? Math.floor(gameEngine.troop_capacity_tot / unit.space) : Infinity;
                
                // Calculate full build time for capacity (time to build max units)
                const fullBuildTimeSeconds = unit.space > 0 ? buildTimeSeconds * maxUnits : Infinity;
                const fullBuildTimeFormatted = unit.space > 0 ? this.formatDuration(fullBuildTimeSeconds) : 'N/A';
                
                // Calculate food/hour at capacity
                // foodUsage is stored as per 24 hours, so divide by 24 to get per hour
                // For units with space = 0, show N/A since capacity is unlimited
                const foodPerHourAtCapacity = unit.space > 0 ? (unit.foodUsage * maxUnits) / 24 : Infinity;
                
                // Get current selected count
                const currentCount = this.selectedUnits.get(unit.name) || 0;
                
                return `
                    <div class="unit-card" data-unit-name="${unit.name}">
                        <div class="unit-header">
                            <label class="unit-checkbox-label">
                                <input type="checkbox" class="unit-checkbox" data-unit-name="${unit.name}" ${currentCount > 0 ? 'checked' : ''}>
                            <img src="./assets/images/ui/${unit.icon}.png" alt="${unit.name}" class="unit-icon-img" onerror="this.style.display='none'" />
                            <div class="unit-name">${unit.name}</div>
                            </label>
                        </div>
                        <div class="unit-stats">
                            <div class="unit-stat-row">
                                <span>Cost:</span>
                                <span>${this.formatCost(unit)}</span>
                            </div>
                            <div class="unit-stat-row">
                                <span>Rate:</span>
                                <span>${buildRatePercent}%</span>
                            </div>
                            <div class="unit-stat-row">
                                <span>Build Time:</span>
                                <span>${buildTimeFormatted}</span>
                            </div>
                            <div class="unit-stat-row">
                                <span>Food/Hour:</span>
                                <span>${this.formatNumber(unit.foodUsage / 24)}</span>
                            </div>
                            <div class="unit-stat-row">
                                <span>UnitSlots:</span>
                                <span>${this.formatNumber(unit.space)}</span>
                            </div>
                            <div class="unit-stat-row">
                                <span>Max Capacity:</span>
                                <span>${unit.space > 0 ? this.formatNumber(maxUnits) : '∞'}</span>
                            </div>
                            <div class="unit-stat-row">
                                <span>Full Build Time:</span>
                                <span>${unit.space > 0 ? fullBuildTimeFormatted : 'N/A'}</span>
                            </div>
                            <div class="unit-stat-row">
                                <span>Food/Hour @ Capacity:</span>
                                <span>${unit.space > 0 ? this.formatNumber(foodPerHourAtCapacity) : 'N/A'}</span>
                            </div>
                        </div>
                        <div class="unit-controls" style="display: ${currentCount > 0 ? 'block' : 'none'};">
                            <div class="unit-slider-container">
                                <label class="unit-slider-label">
                                    <span>Troops:</span>
                                    <input type="number" 
                                           class="unit-count-input" 
                                           data-unit-name="${unit.name}"
                                           min="0" 
                                           max="${unit.space > 0 ? maxUnits : 10000}" 
                                           value="${currentCount}"
                                           step="1">
                                </label>
                                ${this.shouldShowIncrementButtons(unit.name) ? `
                                <div class="unit-increment-buttons">
                                    <button class="unit-increment-btn unit-decrement-btn" data-unit-name="${unit.name}" type="button">-1</button>
                                    <button class="unit-increment-btn unit-increment-btn-plus" data-unit-name="${unit.name}" type="button">+1</button>
                                </div>
                                ` : ''}
                                <input type="range" 
                                       class="unit-slider" 
                                       data-unit-name="${unit.name}"
                                       min="0" 
                                       max="${unit.space > 0 ? maxUnits : 10000}" 
                                       value="${currentCount}"
                                       step="1">
                            </div>
                        </div>
                    </div>
                `;
            }).join('')
            : '<div class="units-empty" style="grid-column: 1 / -1; text-align: center; padding: 40px; color: #aaa; font-style: italic;">No units can be produced. Add military buildings (Guard House, Training Arena, Stable, etc.) to produce units.</div>';
        
        this.container.innerHTML = `
            <div class="military-panel">
                <!-- Production Rates Grid -->
                <div class="production-rates-grid">
                    ${productionRatesGrid}
                </div>
                
                <!-- Troop Percentage Allocation -->
                <div class="troop-percentage-section" id="troop-percentage-section" style="display: none;">
                    <h4>Troop Allocation (%)</h4>
                    <div class="percentage-inputs-container" id="percentage-inputs-container">
                        <!-- Percentage inputs will be dynamically generated here -->
                    </div>
                    <div class="percentage-total">
                        <span>Total:</span>
                        <span class="percentage-total-value" id="percentage-total-value">0%</span>
                    </div>
                </div>
                
                <!-- Units Grid -->
                <div class="units-grid">
                    ${unitRows}
                </div>
                
                <!-- Selection Summary -->
                <div class="selection-summary">
                    <h3>Selection Summary</h3>
                    <div class="summary-main-grid">
                        <div class="summary-main-column">
                            <h4>Stats</h4>
                            <div class="summary-stats">
                                <div class="summary-stat-row">
                                    <span>Total Troops:</span>
                                    <span class="summary-value" id="summary-total-troops">0</span>
                                </div>
                                <div class="summary-stat-row">
                                    <span>Troop Score Used:</span>
                                    <span class="summary-value" id="summary-capacity-used">0</span>
                                    <span>/</span>
                                    <span class="summary-value">${this.formatNumber(gameEngine.troop_capacity_tot)}</span>
                                </div>
                                <div class="summary-stat-row">
                                    <span>Total Food/Hour:</span>
                                    <span class="summary-value" id="summary-food-hour">0</span>
                                </div>
                                <div class="summary-stat-row">
                                    <span>Total Build Time:</span>
                                    <span class="summary-value" id="summary-build-time">0s</span>
                                </div>
                                <div class="summary-stat-row" id="summary-carry-capacity-row" style="display: none;">
                                    <span>Carry Capacity:</span>
                                    <span class="summary-value" id="summary-carry-capacity">0</span>
                                </div>
                                <div class="summary-stat-row" id="summary-transport-capacity-row" style="display: none;">
                                    <span>Galley Carry Used:</span>
                                    <span class="summary-value" id="summary-transport-capacity">0</span>
                                </div>
                            </div>
                        </div>
                        <div class="summary-main-column">
                            <h4>Total Cost</h4>
                            <div class="summary-costs">
                                <div class="summary-cost-row">
                                    <span>Wood:</span>
                                    <span class="summary-value" id="summary-cost-wood">0</span>
                                </div>
                                <div class="summary-cost-row">
                                    <span>Stone:</span>
                                    <span class="summary-value" id="summary-cost-stone">0</span>
                                </div>
                                <div class="summary-cost-row">
                                    <span>Iron:</span>
                                    <span class="summary-value" id="summary-cost-iron">0</span>
                                </div>
                                <div class="summary-cost-row">
                                    <span>Gold:</span>
                                    <span class="summary-value" id="summary-cost-gold">0</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="summary-combat">
                        <h4>Combat Stats</h4>
                        <div class="summary-combat-grid">
                            <div class="summary-combat-column">
                                <div class="summary-combat-label">Attack:</div>
                                <div class="summary-combat-values">
                                    <div class="summary-combat-row">
                                        <span>Infantry:</span>
                                        <span class="summary-value" id="summary-atk-infantry">0</span>
                                    </div>
                                    <div class="summary-combat-row">
                                        <span>Calvary:</span>
                                        <span class="summary-value" id="summary-atk-calvary">0</span>
                                    </div>
                                    <div class="summary-combat-row">
                                        <span>Magic:</span>
                                        <span class="summary-value" id="summary-atk-magic">0</span>
                                    </div>
                                    <div class="summary-combat-row">
                                        <span>Artillery:</span>
                                        <span class="summary-value" id="summary-atk-artillery">0</span>
                                    </div>
                                    <div class="summary-combat-row" id="summary-damage-fortifications-row" style="display: none;">
                                        <span>Damage to Fortifications:</span>
                                        <span class="summary-value" id="summary-damage-fortifications">0</span>
                                    </div>
                                    <div class="summary-combat-row" id="summary-damage-buildings-row" style="display: none;">
                                        <span>Damage to Buildings:</span>
                                        <span class="summary-value" id="summary-damage-buildings">0</span>
                                    </div>
                                </div>
                            </div>
                            <div class="summary-combat-column">
                                <div class="summary-combat-label">Defense:</div>
                                <div class="summary-combat-values">
                                    <div class="summary-combat-row">
                                        <span>vs Infantry:</span>
                                        <span class="summary-value" id="summary-def-infantry">0</span>
                                    </div>
                                    <div class="summary-combat-row">
                                        <span>vs Calvary:</span>
                                        <span class="summary-value" id="summary-def-calvary">0</span>
                                    </div>
                                    <div class="summary-combat-row">
                                        <span>vs Magic:</span>
                                        <span class="summary-value" id="summary-def-magic">0</span>
                                    </div>
                                    <div class="summary-combat-row">
                                        <span>vs Artillery:</span>
                                        <span class="summary-value" id="summary-def-artillery">0</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- Bottom Section -->
                <div class="military-bottom">
                    <div class="troop-capacity">
                        <h3>Troop Capacity</h3>
                        <div class="capacity-value">${this.formatNumber(gameEngine.troop_capacity_tot)}</div>
                    </div>
                    <div class="troop-planner-section">
                        <p class="troop-planner-note">For more planning use the Troop Planner</p>
                        <a href="calculators/troop-planner.php" class="btn-troop-planner">
                        Troop Planner
                    </a>
                    </div>
                </div>
            </div>
        `;
        
        // Setup event listeners
        this.setupEventListeners();
    }
    
    /**
     * Setup event listeners
     */
    setupEventListeners() {
        // Handle unit checkbox changes
        const checkboxes = this.container.querySelectorAll('.unit-checkbox');
        checkboxes.forEach(checkbox => {
            checkbox.addEventListener('change', (e) => {
                const unitName = e.target.dataset.unitName;
                const unitCard = e.target.closest('.unit-card');
                const controls = unitCard.querySelector('.unit-controls');
                
                if (e.target.checked) {
                    // Initialize to 100% of available capacity instead of 0
                    const unit = UNITS.find(u => u.name === unitName);
                    if (!unit) return;
                    
                    // Check if this is a land troop and Galleys are already selected
                    const navalUnits = ['Galley', 'Warship', 'Stinger'];
                    const isLandTroop = !navalUnits.includes(unitName) && unitName !== 'Senator';
                    const galleyCheckbox = this.container.querySelector('.unit-checkbox[data-unit-name="Galley"]');
                    const hasGalleys = galleyCheckbox && galleyCheckbox.checked;
                    
                    let initialValue = 0;
                    
                    if (isLandTroop && hasGalleys) {
                        // Special case: Land troop + Galleys - maximize land troop capacity
                        // Calculate max land troop capacity with Galleys
                        const maxLandTroopCapacity = this.getMaxLandTroopCapacityWithGalleys(unitName);
                        // Calculate how many of this unit can fit
                        initialValue = Math.floor(maxLandTroopCapacity / unit.space);
                    } else if (unitName === 'Galley') {
                        // Galley is being checked - check if there are land troops
                        let landTroopCapacity = 0;
                        const landTroopUnits = new Map(); // Store land troop units and their counts
                        
                        this.selectedUnits.forEach((count, name) => {
                            if (navalUnits.includes(name) || name === 'Senator') return;
                            if (!count || count <= 0) return;
                            const u = UNITS.find(ut => ut.name === name);
                            if (u && u.space > 0) {
                                landTroopCapacity += u.space * count;
                                landTroopUnits.set(name, { unit: u, count: count });
                            }
                        });
                        
                        if (landTroopCapacity > 0) {
                            // Calculate maximum land troop capacity that fits with required Galleys
                            // First, temporarily add Galley to selectedUnits to get accurate calculation
                            // (We'll remove it after calculation)
                            const tempGalleyCount = this.selectedUnits.get('Galley') || 0;
                            
                            // Calculate max land troop capacity with Galleys
                            const maxLandTroopCapacity = this.getMaxLandTroopCapacityWithGalleys();
                            
                            // If current land troop capacity exceeds max, scale down proportionally
                            if (landTroopCapacity > maxLandTroopCapacity) {
                                const scaleFactor = maxLandTroopCapacity / landTroopCapacity;
                                
                                // Scale down all land troops proportionally
                                landTroopUnits.forEach((data, name) => {
                                    const newCount = Math.floor(data.count * scaleFactor);
                                    if (newCount > 0) {
                                        this.selectedUnits.set(name, newCount);
                                        // Update UI
                                        const slider = this.container.querySelector(`.unit-slider[data-unit-name="${name}"]`);
                                        const input = this.container.querySelector(`.unit-count-input[data-unit-name="${name}"]`);
                                        if (slider) slider.value = newCount;
                                        if (input) input.value = newCount;
                                        this.updateUnitDisplay(name, newCount);
                                    } else {
                                        // If scaled down to 0, remove from selection
                                        this.selectedUnits.delete(name);
                                        const checkbox = this.container.querySelector(`.unit-checkbox[data-unit-name="${name}"]`);
                                        if (checkbox) {
                                            checkbox.checked = false;
                                            const unitCard = checkbox.closest('.unit-card');
                                            const controls = unitCard?.querySelector('.unit-controls');
                                            if (controls) controls.style.display = 'none';
                                        }
                                    }
                                });
                                
                                // Recalculate land troop capacity after scaling
                                landTroopCapacity = 0;
                                this.selectedUnits.forEach((count, name) => {
                                    if (navalUnits.includes(name) || name === 'Senator') return;
                                    if (!count || count <= 0) return;
                                    const u = UNITS.find(ut => ut.name === name);
                                    if (u && u.space > 0) {
                                        landTroopCapacity += u.space * count;
                                    }
                                });
                                
                                // Update slider max values after scaling
                                this.updateSliderMaxValues();
                            }
                            
                            // Auto-calculate required Galleys based on adjusted land troop capacity
                            const galleyTransportCapacity = this.getTransportCapacity('Galley'); // 500 per Galley
                            initialValue = Math.ceil(landTroopCapacity / galleyTransportCapacity);
                        } else {
                            // No land troops - set to max capacity like other naval units
                            const currentCapacityUsed = this.getCurrentCapacityUsed();
                            const availableCapacity = this.gameEngine.troop_capacity_tot - currentCapacityUsed;
                            initialValue = Math.floor(availableCapacity / unit.space);
                        }
                    } else {
                        // Standard case: Calculate max allowed for this unit
                        const currentCapacityUsed = this.getCurrentCapacityUsed();
                        const capacityWithoutThisUnit = currentCapacityUsed;
                        const maxAllowed = this.getMaxAllowedForUnit(unitName, capacityWithoutThisUnit);
                        initialValue = maxAllowed;
                    }
                    
                    // Ensure at least 1 if unit can be built (space > 0)
                    if (unit.space > 0 && initialValue < 1) {
                        initialValue = 1;
                    }
                    
                    this.selectedUnits.set(unitName, initialValue);
                    controls.style.display = 'block';
                    
                    // Sync slider value
                    const slider = unitCard.querySelector('.unit-slider');
                    if (slider) {
                        slider.value = initialValue;
                    }
                    // Sync input field
                    this.updateUnitDisplay(unitName, initialValue);
                    
                    // Update all slider max values
                    this.updateSliderMaxValues();
                    // Update automatic Galleys if needed (for Galley + land troops case)
                    this.updateAutomaticGalleys();
                    this.updateSummary();
                } else {
                    // Remove from selection - delete from map to ensure capacity calculations are correct
                    this.selectedUnits.delete(unitName);
                    controls.style.display = 'none';
                    // Reset slider
                    const slider = unitCard.querySelector('.unit-slider');
                    if (slider) {
                        slider.value = 0;
                    }
                    // Reset input field
                    this.updateUnitDisplay(unitName, 0);
                    // Update all slider max values since capacity is now freed
                    this.updateSliderMaxValues();
                    // Update summary to reflect the removal
                    this.updateSummary();
                }
            });
        });
        
        // Handle slider changes
        const sliders = this.container.querySelectorAll('.unit-slider');
        sliders.forEach(slider => {
            slider.addEventListener('input', (e) => {
                // Skip if we're updating Galleys automatically
                if (this._updatingGalleys) return;
                
                const unitName = e.target.dataset.unitName;
                const unit = UNITS.find(u => u.name === unitName);
                if (!unit) return;
                
                let requestedCount = parseInt(e.target.value) || 0;
                
                // Calculate current capacity used (excluding this unit's current count)
                const currentCount = this.selectedUnits.get(unitName) || 0;
                const currentCapacityUsed = this.getCurrentCapacityUsed();
                const capacityWithoutThisUnit = currentCapacityUsed - (unit.space * currentCount);
                
                // Calculate max allowed for this unit based on remaining capacity
                const maxAllowed = this.getMaxAllowedForUnit(unitName, capacityWithoutThisUnit);
                
                // Cap the value if it exceeds capacity
                if (requestedCount > maxAllowed) {
                    requestedCount = maxAllowed;
                    e.target.value = requestedCount;
                }
                
                // If count is 0, remove from map instead of setting to 0
                // Keep checkbox checked and controls visible - user can adjust slider back up
                if (requestedCount <= 0) {
                    this.selectedUnits.delete(unitName);
                } else {
                    this.selectedUnits.set(unitName, requestedCount);
                }
                this.updateUnitDisplay(unitName, requestedCount);
                
                // Sync input field with slider
                const input = this.container.querySelector(`.unit-count-input[data-unit-name="${unitName}"]`);
                if (input) {
                    input.value = requestedCount;
                }
                
                // Update all slider max values to reflect new available capacity
                this.updateSliderMaxValues();
                // Update automatic Galleys if needed
                this.updateAutomaticGalleys();
                this.updateSummary();
            });
        });
        
        // Handle input field changes
        const countInputs = this.container.querySelectorAll('.unit-count-input');
        countInputs.forEach(input => {
            input.addEventListener('input', (e) => {
                // Skip if we're updating Galleys automatically
                if (this._updatingGalleys) return;
                
                const unitName = e.target.dataset.unitName;
                const unit = UNITS.find(u => u.name === unitName);
                if (!unit) return;
                
                let requestedCount = parseInt(e.target.value) || 0;
                
                // Ensure non-negative
                if (requestedCount < 0) {
                    requestedCount = 0;
                    e.target.value = 0;
                }
                
                // Calculate current capacity used (excluding this unit's current count)
                const currentCount = this.selectedUnits.get(unitName) || 0;
                const currentCapacityUsed = this.getCurrentCapacityUsed();
                const capacityWithoutThisUnit = currentCapacityUsed - (unit.space * currentCount);
                
                // Calculate max allowed for this unit based on remaining capacity
                const maxAllowed = this.getMaxAllowedForUnit(unitName, capacityWithoutThisUnit);
                
                // Cap the value if it exceeds capacity
                if (requestedCount > maxAllowed) {
                    requestedCount = maxAllowed;
                    e.target.value = requestedCount;
                }
                
                // If count is 0, remove from map instead of setting to 0
                // Keep checkbox checked and controls visible - user can adjust input back up
                if (requestedCount <= 0) {
                    this.selectedUnits.delete(unitName);
                } else {
                    this.selectedUnits.set(unitName, requestedCount);
                }
                
                // Sync slider with input
                const slider = this.container.querySelector(`.unit-slider[data-unit-name="${unitName}"]`);
                if (slider) {
                    slider.value = requestedCount;
                }
                
                // Update all slider max values to reflect new available capacity
                this.updateSliderMaxValues();
                // Update automatic Galleys if needed
                this.updateAutomaticGalleys();
                this.updateSummary();
            });
            
            // Handle blur event to ensure value is valid when user leaves the field
            input.addEventListener('blur', (e) => {
                // Skip if we're updating Galleys automatically
                if (this._updatingGalleys) return;
                
                const unitName = e.target.dataset.unitName;
                const value = parseInt(e.target.value) || 0;
                const unit = UNITS.find(u => u.name === unitName);
                if (!unit) return;
                
                // Calculate max allowed
                const currentCount = this.selectedUnits.get(unitName) || 0;
                const currentCapacityUsed = this.getCurrentCapacityUsed();
                const capacityWithoutThisUnit = currentCapacityUsed - (unit.space * currentCount);
                const maxAllowed = this.getMaxAllowedForUnit(unitName, capacityWithoutThisUnit);
                
                let finalValue = Math.max(0, Math.min(value, maxAllowed));
                if (value !== finalValue) {
                    e.target.value = finalValue;
                    
                    // If count is 0, remove from map instead of setting to 0
                    // Keep checkbox checked and controls visible - user can adjust input back up
                    if (finalValue <= 0) {
                        this.selectedUnits.delete(unitName);
                    } else {
                        this.selectedUnits.set(unitName, finalValue);
                    }
                    
                    // Sync slider
                    const slider = this.container.querySelector(`.unit-slider[data-unit-name="${unitName}"]`);
                    if (slider) {
                        slider.value = finalValue;
                    }
                    
                    this.updateSliderMaxValues();
                    // Update automatic Galleys if needed
                    this.updateAutomaticGalleys();
                    this.updateSummary();
                }
            });
        });
        
        // Handle increment/decrement button clicks
        // Use event delegation to handle buttons that may be dynamically shown/hidden
        // Remove old listener if it exists to prevent duplicate handlers
        if (this._incrementButtonHandler) {
            this.container.removeEventListener('click', this._incrementButtonHandler);
        }
        
        this._incrementButtonHandler = (e) => {
            // Check if the clicked element is an increment button
            if (!e.target.classList.contains('unit-increment-btn')) return;
            
            e.preventDefault();
            e.stopPropagation();
            
            const unitName = e.target.dataset.unitName;
            if (!unitName) return;
            
            const unit = UNITS.find(u => u.name === unitName);
            if (!unit) return;
            
            const isDecrement = e.target.classList.contains('unit-decrement-btn');
            // Get current count - if unit is not in map, it means count is 0
            const currentCount = this.selectedUnits.get(unitName) || 0;
            const currentCapacityUsed = this.getCurrentCapacityUsed();
            const capacityWithoutThisUnit = currentCapacityUsed - (unit.space * currentCount);
            const maxAllowed = this.getMaxAllowedForUnit(unitName, capacityWithoutThisUnit);
            
            let newCount = isDecrement ? currentCount - 1 : currentCount + 1;
            newCount = Math.max(0, Math.min(newCount, maxAllowed));
            
            // If count is 0, remove from map instead of setting to 0
            // Keep checkbox checked and controls visible - user can adjust count back up
            if (newCount <= 0) {
                this.selectedUnits.delete(unitName);
            } else {
                this.selectedUnits.set(unitName, newCount);
            }
            this.updateUnitDisplay(unitName, newCount);
            
            // Update slider value
            const slider = this.container.querySelector(`.unit-slider[data-unit-name="${unitName}"]`);
            if (slider) {
                slider.value = newCount;
            }
            
            // Sync input field (updateUnitDisplay already does this, but ensure it's synced)
            const input = this.container.querySelector(`.unit-count-input[data-unit-name="${unitName}"]`);
            if (input) {
                input.value = newCount;
            }
            
            // Update all slider max values
            this.updateSliderMaxValues();
            // Update automatic Galleys if needed
            this.updateAutomaticGalleys();
            this.updateSummary();
        };
        
        this.container.addEventListener('click', this._incrementButtonHandler);
        
        // Initial summary update and slider max values
        this.updateSliderMaxValues();
        this.updateSummary();
    }
    
    /**
     * Get current total capacity used by all selected units
     */
    getCurrentCapacityUsed() {
        let capacityUsed = 0;
        this.selectedUnits.forEach((count, unitName) => {
            // Skip zero or negative values - these shouldn't count toward capacity
            if (!count || count <= 0) return;
            const unit = UNITS.find(u => u.name === unitName);
            if (unit && unit.space > 0) {
                capacityUsed += unit.space * count;
            }
        });
        return capacityUsed;
    }
    
    /**
     * Calculate maximum land troop capacity that can fit with required Galleys
     * Returns the maximum land troop capacity considering that Galleys will be auto-added
     * @param {string} excludeUnitName - Optional unit name to exclude from calculation (for per-unit max calculations)
     */
    getMaxLandTroopCapacityWithGalleys(excludeUnitName = null) {
        const { gameEngine } = this;
        const totalCapacity = gameEngine.troop_capacity_tot;
        
        // Check if Galleys checkbox is checked
        const galleyCheckbox = this.container.querySelector('.unit-checkbox[data-unit-name="Galley"]');
        if (!galleyCheckbox || !galleyCheckbox.checked) {
            return totalCapacity; // No Galleys, so all capacity available for land troops
        }
        
        // Calculate capacity used by non-land troops (naval units, Senator, etc.)
        const navalUnits = ['Galley', 'Warship', 'Stinger'];
        let nonLandTroopCapacity = 0;
        
        this.selectedUnits.forEach((count, unitName) => {
            // Skip zero or negative values - these shouldn't count toward capacity
            if (!count || count <= 0) return;
            // Count naval units and Senator
            if (navalUnits.includes(unitName) || unitName === 'Senator') {
                const unit = UNITS.find(u => u.name === unitName);
                if (unit) {
                    nonLandTroopCapacity += unit.space * count;
                }
            }
        });
        
        // Available capacity for land troops + required Galleys
        const availableForLandAndGalleys = totalCapacity - nonLandTroopCapacity;
        
        // Solve: landTroopCapacity + Math.ceil(landTroopCapacity / 500) * 100 <= availableForLandAndGalleys
        // Use binary search approach to find maximum land troop capacity more precisely
        const galleyTransportCapacity = this.getTransportCapacity('Galley'); // 500 per Galley
        const galleyUnit = UNITS.find(u => u.name === 'Galley');
        const galleySpace = galleyUnit?.space || 100;
        
        // Binary search for optimal land troop capacity
        let low = 0;
        let high = availableForLandAndGalleys;
        let maxLandTroopCapacity = 0;
        
        // Use binary search with fine-grained steps for precision
        while (low <= high) {
            const mid = Math.floor((low + high) / 2);
            const testRequiredGalleys = Math.ceil(mid / galleyTransportCapacity);
            const testRequiredGalleyCapacity = testRequiredGalleys * galleySpace;
            const testTotal = mid + testRequiredGalleyCapacity;
            
            if (testTotal <= availableForLandAndGalleys) {
                maxLandTroopCapacity = mid;
                low = mid + 1; // Try to find a larger value
            } else {
                high = mid - 1; // Too large, reduce
            }
        }
        
        // Fine-tune: check values around the found maximum to ensure we get the absolute maximum
        // Check a wider range with smaller steps to find the true maximum
        let bestCapacity = maxLandTroopCapacity;
        let bestTotal = maxLandTroopCapacity + Math.ceil(maxLandTroopCapacity / galleyTransportCapacity) * galleySpace;
        
        // Check values around the found maximum with fine-grained steps
        for (let offset = -200; offset <= 200; offset += 1) {
            const testCapacity = maxLandTroopCapacity + offset;
            if (testCapacity < 0 || testCapacity > availableForLandAndGalleys) continue;
            
            const testRequiredGalleys = Math.ceil(testCapacity / galleyTransportCapacity);
            const testRequiredGalleyCapacity = testRequiredGalleys * galleySpace;
            const testTotal = testCapacity + testRequiredGalleyCapacity;
            
            if (testTotal <= availableForLandAndGalleys && testTotal > bestTotal) {
                bestCapacity = testCapacity;
                bestTotal = testTotal;
            }
        }
        
        maxLandTroopCapacity = bestCapacity;
        
        return Math.max(0, maxLandTroopCapacity);
    }
    
    /**
     * Get maximum allowed count for a specific unit based on available capacity
     */
    getMaxAllowedForUnit(unitName, capacityAlreadyUsed = null) {
        const unit = UNITS.find(u => u.name === unitName);
        if (!unit) return 0;
        
        // Units with space = 0 don't count toward capacity
        if (unit.space === 0) {
            return 10000; // Effectively unlimited
        }
        
        const { gameEngine } = this;
        const totalCapacity = gameEngine.troop_capacity_tot;
        
        // Check if this is a land troop unit and Galleys are selected
        const navalUnits = ['Galley', 'Warship', 'Stinger'];
        const isLandTroop = !navalUnits.includes(unitName) && unitName !== 'Senator';
        const galleyCheckbox = this.container.querySelector('.unit-checkbox[data-unit-name="Galley"]');
        const hasGalleys = galleyCheckbox && galleyCheckbox.checked;
        
        // If this is a land troop and Galleys are selected, use the max land troop capacity calculation
        if (isLandTroop && hasGalleys) {
            // Calculate current land troop capacity (excluding this unit and Galleys)
            let currentLandTroopCapacity = 0;
            this.selectedUnits.forEach((count, name) => {
                if (navalUnits.includes(name) || name === 'Senator' || name === unitName) {
                    return;
                }
                // Skip zero or negative values - these shouldn't count toward capacity
                if (!count || count <= 0) return;
                const u = UNITS.find(ut => ut.name === name);
                if (u && u.space > 0) {
                    currentLandTroopCapacity += u.space * count;
                }
            });
            
            // Get max land troop capacity with Galleys (this already accounts for required Galleys)
            const maxLandTroopCapacity = this.getMaxLandTroopCapacityWithGalleys(unitName);
            
            // Calculate available capacity for this unit
            const availableForThisUnit = maxLandTroopCapacity - currentLandTroopCapacity;
            
            // Calculate max units that can fit
            const maxUnits = Math.floor(availableForThisUnit / unit.space);
            
            return Math.max(0, maxUnits);
        }
        
        // Standard calculation for non-land troops or when Galleys aren't selected
        // For naval units (Warship, Galley, Stinger), calculate max based on total capacity
        // minus only other naval units (not land troops, as they use different capacity in water cities)
        if (navalUnits.includes(unitName)) {
            // For naval units, only subtract capacity used by other naval units
            let navalCapacityUsed = 0;
            this.selectedUnits.forEach((count, name) => {
                if (name === unitName) return; // Don't count this unit
                // Skip zero or negative values - these shouldn't count toward capacity
                if (!count || count <= 0) return;
                if (navalUnits.includes(name)) {
                    const u = UNITS.find(ut => ut.name === name);
                    if (u && u.space > 0) {
                        navalCapacityUsed += u.space * count;
                    }
                }
            });
            
            const availableCapacity = totalCapacity - navalCapacityUsed;
            const maxUnits = Math.floor(availableCapacity / unit.space);
            return Math.max(0, maxUnits);
        }
        
        // For land troops, calculate capacity already used (if not provided)
        if (capacityAlreadyUsed === null) {
            capacityAlreadyUsed = this.getCurrentCapacityUsed();
        }
        
        // Calculate available capacity
        const availableCapacity = totalCapacity - capacityAlreadyUsed;
        
        // Calculate max units that can fit in available capacity
        const maxUnits = Math.floor(availableCapacity / unit.space);
        
        return Math.max(0, maxUnits);
    }
    
    /**
     * Update all slider max values based on current capacity usage
     */
    updateSliderMaxValues() {
        const { gameEngine } = this;
        const currentCapacityUsed = this.getCurrentCapacityUsed();
        
        // Update all visible sliders (not just those in selectedUnits)
        const allSliders = this.container.querySelectorAll('.unit-slider');
        allSliders.forEach(slider => {
            const unitName = slider.dataset.unitName;
            const unit = UNITS.find(u => u.name === unitName);
            if (!unit) return;
            
            // Calculate capacity used by other units (excluding this one)
            const currentCount = this.selectedUnits.get(unitName) || 0;
            const capacityWithoutThisUnit = currentCapacityUsed - (unit.space * currentCount);
            
            // Get max allowed for this unit
            const maxAllowed = this.getMaxAllowedForUnit(unitName, capacityWithoutThisUnit);
            
            // Update slider max
            slider.max = unit.space > 0 ? maxAllowed : 10000;
            
            // Update input field max
            const input = this.container.querySelector(`.unit-count-input[data-unit-name="${unitName}"]`);
            if (input) {
                input.max = unit.space > 0 ? maxAllowed : 10000;
            }
            
            // If current value exceeds new max, cap it
            const currentValue = parseInt(slider.value) || 0;
            if (currentValue > maxAllowed && unit.space > 0) {
                slider.value = maxAllowed;
                this.selectedUnits.set(unitName, maxAllowed);
                this.updateUnitDisplay(unitName, maxAllowed);
            }
        });
    }
    
    /**
     * Update unit display count
     */
    updateUnitDisplay(unitName, count) {
        const input = this.container.querySelector(`.unit-count-input[data-unit-name="${unitName}"]`);
        if (input) {
            input.value = count;
        }
    }
    
    /**
     * Get carry capacity (loot capacity) for a unit
     * This is how much loot/resources a unit can carry
     * Values from troops.md
     */
    getCarryCapacity(unitName) {
        // Carry capacity values for loot from troops.md
        const carryCapacityMap = {
            "Guard": 0,           // No carry capacity
            "Vanquisher": 10,
            "Ranger": 10,
            "Triari": 20,
            "Scout": 0,
            "Arbalist": 15,
            "Horseman": 15,
            "Sorceres": 5,        // Sorcerer in docs
            "Druid": 10,
            "Priestess": 10,
            "Praetor": 20,
            "Senator": 0,
            "Battering Ram": 0,   // No carry capacity
            "Ballista": 0,        // No carry capacity
            "Scorpion": 0,        // No carry capacity
            "Stinger": 1500,
            "Galley": 1000,
            "Warship": 3000
        };
        return carryCapacityMap[unitName] || 0;
    }
    
    /**
     * Get transport capacity for a unit (how many units it can transport)
     * Only Galleys have transport capacity
     * Values from troops.md
     */
    getTransportCapacity(unitName) {
        // Transport capacity - how many units can be transported
        // From troops.md: Galley has transport capacity of 500
        if (unitName === "Galley") {
            return 500;
        }
        return 0;
    }
    
    /**
     * Check if unit should show increment/decrement buttons
     * For units with very large numbers (Rams, Senators, Galleys)
     */
    shouldShowIncrementButtons(unitName) {
        return unitName === "Battering Ram" || unitName === "Senator" || unitName === "Galley";
    }
    
    /**
     * Get damage to city for artillery units
     * Returns an object with fortifications and buildings damage
     * Values from troops.md
     */
    getDamageToCity(unitName) {
        // Damage to city values for artillery units from troops.md
        const damageToCityMap = {
            "Battering Ram": { fortifications: 250, buildings: 0 },
            "Ballista": { fortifications: 0, buildings: 0 },           // No damage to city
            "Scorpion": { fortifications: 0, buildings: 250 },
            "Stinger": { fortifications: 0, buildings: 0 },            // No damage to city
            "Galley": { fortifications: 0, buildings: 0 },             // No damage to city
            "Warship": { fortifications: 4000, buildings: 4000 }       // Both types
        };
        return damageToCityMap[unitName] || { fortifications: 0, buildings: 0 };
    }
    
    /**
     * Get HTML display for damage to city stat
     * Shows fortifications and buildings separately when applicable
     */
    getDamageToCityDisplay(unitName) {
        const damage = this.getDamageToCity(unitName);
        const hasFortifications = damage.fortifications > 0;
        const hasBuildings = damage.buildings > 0;
        const hasBoth = hasFortifications && hasBuildings;
        
        // If no damage to city, don't show anything
        if (!hasFortifications && !hasBuildings) {
            return '';
        }
        
        // If both types have the same value, show combined
        if (hasBoth && damage.fortifications === damage.buildings) {
            return `
                            <div class="unit-stat-row">
                                <span>Damage to City:</span>
                                <span>${this.formatNumber(damage.fortifications)} (Fortifications & Buildings)</span>
                            </div>
                            `;
        }
        
        // Show separate rows for different values
        let html = '';
        if (hasFortifications) {
            html += `
                            <div class="unit-stat-row">
                                <span>Damage to Fortifications:</span>
                                <span>${this.formatNumber(damage.fortifications)}</span>
                            </div>
                            `;
        }
        if (hasBuildings) {
            html += `
                            <div class="unit-stat-row">
                                <span>Damage to Buildings:</span>
                                <span>${this.formatNumber(damage.buildings)}</span>
                            </div>
                            `;
        }
        return html;
    }
    
    /**
     * Automatically calculate and update Galleys based on land troop capacity
     * Called whenever troop counts change (sliders, inputs, percentages)
     */
    updateAutomaticGalleys() {
        // Prevent infinite loops
        if (this._updatingGalleys) {
            return;
        }
        
        // Check if Galleys checkbox is checked
        const galleyCheckbox = this.container.querySelector('.unit-checkbox[data-unit-name="Galley"]');
        if (!galleyCheckbox || !galleyCheckbox.checked) {
            return; // Galleys not selected, no need to update
        }
        
        // Set flag to prevent infinite loops
        this._updatingGalleys = true;
        
        // Calculate total land troop capacity (excluding naval units: Galley, Warship, Stinger)
        let landTroopCapacity = 0;
        const navalUnits = ['Galley', 'Warship', 'Stinger'];
        
        this.selectedUnits.forEach((count, unitName) => {
            // Skip naval units and Senator
            if (navalUnits.includes(unitName) || unitName === 'Senator') {
                return;
            }
            // Skip zero or negative values - these shouldn't count toward capacity
            if (!count || count <= 0) return;
            
            const unit = UNITS.find(u => u.name === unitName);
            if (unit && unit.space > 0) {
                landTroopCapacity += unit.space * count;
            }
        });
        
        // If no land troops, don't auto-calculate Galleys - let user control them manually
        // Galleys can be used as normal naval units in navy-only cities
        if (landTroopCapacity === 0) {
            // Clear flag and return - don't interfere with manual Galley control
            this._updatingGalleys = false;
            return;
        }
        
        // Only auto-calculate Galleys when there are land troops
        // Calculate required Galleys: one Galley per 500 unit slots
        const galleyTransportCapacity = this.getTransportCapacity('Galley'); // 500 per Galley
        const requiredGalleys = Math.ceil(landTroopCapacity / galleyTransportCapacity);
        
        // Get current Galley count
        const currentGalleyCount = this.selectedUnits.get('Galley') || 0;
        
        // Only update if the count changed (to avoid infinite loops)
        if (currentGalleyCount !== requiredGalleys) {
            this.selectedUnits.set('Galley', requiredGalleys);
            this.updateUnitDisplay('Galley', requiredGalleys);
            
            // Update slider
            const slider = this.container.querySelector(`.unit-slider[data-unit-name="Galley"]`);
            if (slider) {
                slider.value = requiredGalleys;
            }
            
            // Update input field
            const input = this.container.querySelector(`.unit-count-input[data-unit-name="Galley"]`);
            if (input) {
                input.value = requiredGalleys;
            }
        }
        
        // Clear flag
        this._updatingGalleys = false;
    }
    
    /**
     * Update selection summary
     */
    updateSummary() {
        const { gameEngine } = this;
        let totalTroops = 0;
        let capacityUsed = 0;
        let totalFoodHour = 0;
        let totalBuildTime = 0;
        let totalCarryCapacity = 0; // Loot capacity
        let totalTransportCapacity = 0; // Unit transport capacity
        
        // Resource costs
        let totalWood = 0;
        let totalStone = 0;
        let totalIron = 0;
        let totalGold = 0;
        
        // Attack values by type
        const attack = {
            'None': 0,
            'Infantry': 0,
            'Calvary': 0,
            'Magic': 0,
            'Artillery': 0
        };
        
        // Defense values
        let defInfantry = 0;
        let defCalvary = 0;
        let defMagic = 0;
        let defArtillery = 0;
        
        // Damage to city values
        let totalDamageFortifications = 0;
        let totalDamageBuildings = 0;
        
        this.selectedUnits.forEach((count, unitName) => {
            if (count <= 0) return;
            
            const unit = UNITS.find(u => u.name === unitName);
            if (!unit) return;
            
            // Total Troops = Unit Slots used (space * count)
            totalTroops += unit.space * count;
            capacityUsed += unit.space * count;
            // foodUsage is stored as per 24 hours, so divide by 24 to get per hour
            totalFoodHour += (unit.foodUsage * count) / 24;
            
            // Carry capacity (loot capacity for all units)
            const unitCarryCapacity = this.getCarryCapacity(unitName);
            if (unitCarryCapacity > 0) {
                totalCarryCapacity += unitCarryCapacity * count;
            }
            
            // Transport capacity (for Galleys only - how many units they can transport)
            const unitTransportCapacity = this.getTransportCapacity(unitName);
            if (unitTransportCapacity > 0) {
                totalTransportCapacity += unitTransportCapacity * count;
            }
            
            // Resource costs
            totalWood += unit.wood * count;
            totalStone += unit.stone * count;
            totalIron += unit.iron * count;
            totalGold += unit.gold * count;
            
            // Attack values
            attack[unit.attackType] += unit.attackValue * count;
            
            // Damage to city (for artillery units)
            const damageToCity = this.getDamageToCity(unitName);
            totalDamageFortifications += damageToCity.fortifications * count;
            totalDamageBuildings += damageToCity.buildings * count;
            
            // Defense values
            defInfantry += unit.defInfantry * count;
            defCalvary += unit.defCalvary * count;
            defMagic += unit.defMagic * count;
            defArtillery += unit.defArtillery * count;
            
            const buildRate = unit.getBuildRate(gameEngine);
            if (buildRate > 0) {
                const buildTimeSeconds = unit.productionSpeed / buildRate;
                totalBuildTime += buildTimeSeconds * count;
            }
        });
        
        // Update summary display
        const totalTroopsEl = this.container.querySelector('#summary-total-troops');
        const capacityUsedEl = this.container.querySelector('#summary-capacity-used');
        const foodHourEl = this.container.querySelector('#summary-food-hour');
        const buildTimeEl = this.container.querySelector('#summary-build-time');
        
        if (totalTroopsEl) totalTroopsEl.textContent = this.formatNumber(totalTroops);
        if (capacityUsedEl) capacityUsedEl.textContent = this.formatNumber(capacityUsed);
        if (foodHourEl) foodHourEl.textContent = this.formatNumber(totalFoodHour);
        if (buildTimeEl) buildTimeEl.textContent = this.formatDuration(totalBuildTime);
        
        // Update carry capacity display (loot capacity - show if any units have it)
        const carryCapacityRow = this.container.querySelector('#summary-carry-capacity-row');
        const carryCapacityEl = this.container.querySelector('#summary-carry-capacity');
        if (carryCapacityRow && carryCapacityEl) {
            if (totalCarryCapacity > 0) {
                carryCapacityRow.style.display = 'flex';
                carryCapacityEl.textContent = this.formatNumber(totalCarryCapacity);
            } else {
                carryCapacityRow.style.display = 'none';
            }
        }
        
        // Update transport capacity display (only show if Galleys are selected)
        // Show as: land troop capacity (unitslots used) / total galley transportation capacity
        const transportCapacityRow = this.container.querySelector('#summary-transport-capacity-row');
        const transportCapacityEl = this.container.querySelector('#summary-transport-capacity');
        if (transportCapacityRow && transportCapacityEl) {
            if (totalTransportCapacity > 0) {
                // Calculate land troop capacity (unitslots used by land troops)
                let landTroopCapacity = 0;
                const navalUnits = ['Galley', 'Warship', 'Stinger'];
                this.selectedUnits.forEach((count, unitName) => {
                    // Skip naval units and Senator
                    if (navalUnits.includes(unitName) || unitName === 'Senator') {
                        return;
                    }
                    // Skip zero or negative values - these shouldn't count toward capacity
                    if (!count || count <= 0) return;
                    const unit = UNITS.find(u => u.name === unitName);
                    if (unit && unit.space > 0) {
                        landTroopCapacity += unit.space * count;
                    }
                });
                
                transportCapacityRow.style.display = 'flex';
                transportCapacityEl.textContent = `${this.formatNumber(landTroopCapacity)} / ${this.formatNumber(totalTransportCapacity)}`;
            } else {
                transportCapacityRow.style.display = 'none';
            }
        }
        
        // Update cost display
        const woodEl = this.container.querySelector('#summary-cost-wood');
        const stoneEl = this.container.querySelector('#summary-cost-stone');
        const ironEl = this.container.querySelector('#summary-cost-iron');
        const goldEl = this.container.querySelector('#summary-cost-gold');
        
        if (woodEl) woodEl.textContent = this.formatNumber(totalWood);
        if (stoneEl) stoneEl.textContent = this.formatNumber(totalStone);
        if (ironEl) ironEl.textContent = this.formatNumber(totalIron);
        if (goldEl) goldEl.textContent = this.formatNumber(totalGold);
        
        // Update attack display
        const atkInfantryEl = this.container.querySelector('#summary-atk-infantry');
        const atkCalvaryEl = this.container.querySelector('#summary-atk-calvary');
        const atkMagicEl = this.container.querySelector('#summary-atk-magic');
        const atkArtilleryEl = this.container.querySelector('#summary-atk-artillery');
        
        if (atkInfantryEl) atkInfantryEl.textContent = this.formatNumber(attack['Infantry']);
        if (atkCalvaryEl) atkCalvaryEl.textContent = this.formatNumber(attack['Calvary']);
        if (atkMagicEl) atkMagicEl.textContent = this.formatNumber(attack['Magic']);
        if (atkArtilleryEl) atkArtilleryEl.textContent = this.formatNumber(attack['Artillery']);
        
        // Update damage to city display (only show if there's damage)
        const damageFortificationsRow = this.container.querySelector('#summary-damage-fortifications-row');
        const damageFortificationsEl = this.container.querySelector('#summary-damage-fortifications');
        const damageBuildingsRow = this.container.querySelector('#summary-damage-buildings-row');
        const damageBuildingsEl = this.container.querySelector('#summary-damage-buildings');
        
        if (damageFortificationsRow && damageFortificationsEl) {
            if (totalDamageFortifications > 0) {
                damageFortificationsRow.style.display = 'flex';
                damageFortificationsEl.textContent = this.formatNumber(totalDamageFortifications);
            } else {
                damageFortificationsRow.style.display = 'none';
            }
        }
        
        if (damageBuildingsRow && damageBuildingsEl) {
            if (totalDamageBuildings > 0) {
                damageBuildingsRow.style.display = 'flex';
                damageBuildingsEl.textContent = this.formatNumber(totalDamageBuildings);
            } else {
                damageBuildingsRow.style.display = 'none';
            }
        }
        
        // Update defense display
        const defInfantryEl = this.container.querySelector('#summary-def-infantry');
        const defCalvaryEl = this.container.querySelector('#summary-def-calvary');
        const defMagicEl = this.container.querySelector('#summary-def-magic');
        const defArtilleryEl = this.container.querySelector('#summary-def-artillery');
        
        if (defInfantryEl) defInfantryEl.textContent = this.formatNumber(defInfantry);
        if (defCalvaryEl) defCalvaryEl.textContent = this.formatNumber(defCalvary);
        if (defMagicEl) defMagicEl.textContent = this.formatNumber(defMagic);
        if (defArtilleryEl) defArtilleryEl.textContent = this.formatNumber(defArtillery);
        
        // Update percentage allocation section (only if not already updating)
        if (!this._updatingPercentages) {
            this.updatePercentageAllocation();
        }
    }
    
    /**
     * Get eligible units for percentage allocation
     * Excludes: Senator, Galley, and Ram (unless Scorpions are also selected)
     * Returns units that have their checkbox checked, regardless of count
     * Includes Galleys automatically if selected and there are land troops
     */
    getEligibleUnitsForPercentage() {
        const eligibleUnits = [];
        
        // Check if Scorpions checkbox is checked
        const scorpionCheckbox = this.container.querySelector('.unit-checkbox[data-unit-name="Scorpion"]');
        const hasScorpions = scorpionCheckbox && scorpionCheckbox.checked;
        
        // Check if Galleys checkbox is checked
        const galleyCheckbox = this.container.querySelector('.unit-checkbox[data-unit-name="Galley"]');
        const hasGalleys = galleyCheckbox && galleyCheckbox.checked;
        
        // Get all unit checkboxes
        const allCheckboxes = this.container.querySelectorAll('.unit-checkbox');
        
        // First pass: collect land troops (non-naval units)
        const landTroopUnits = [];
        allCheckboxes.forEach(checkbox => {
            const unitName = checkbox.dataset.unitName;
            if (!checkbox.checked) return;
            
            // Always exclude Senator
            if (unitName === 'Senator') {
                return;
            }
            
            // Exclude naval units (Galley, Warship, Stinger)
            if (unitName === 'Galley' || unitName === 'Warship' || unitName === 'Stinger') {
                return;
            }
            
            // Exclude Ram unless Scorpions are selected
            if (unitName === 'Battering Ram' && !hasScorpions) {
                return;
            }
            
            landTroopUnits.push(unitName);
        });
        
        // Add land troops to eligible units
        eligibleUnits.push(...landTroopUnits);
        
        // Add Galleys if checkbox is checked and there are land troops
        if (hasGalleys && landTroopUnits.length > 0) {
            eligibleUnits.push('Galley');
        }
        
        return eligibleUnits;
    }
    
    /**
     * Update the percentage allocation section
     */
    updatePercentageAllocation() {
        const eligibleUnits = this.getEligibleUnitsForPercentage();
        const percentageSection = this.container.querySelector('#troop-percentage-section');
        const inputsContainer = this.container.querySelector('#percentage-inputs-container');
        const totalValueEl = this.container.querySelector('#percentage-total-value');
        
        if (!percentageSection || !inputsContainer || !totalValueEl) return;
        
        // Show/hide section based on eligible units count
        if (eligibleUnits.length < 2) {
            percentageSection.style.display = 'none';
            return;
        }
        
        percentageSection.style.display = 'block';
        
        // Calculate current total capacity used by eligible units
        let totalEligibleCapacity = 0;
        eligibleUnits.forEach(unitName => {
            const count = this.selectedUnits.get(unitName) || 0;
            const unit = UNITS.find(u => u.name === unitName);
            if (unit) {
                totalEligibleCapacity += unit.space * count;
            }
        });
        
        // If no capacity is used yet, default to equal distribution
        const defaultPercentage = 100 / eligibleUnits.length;
        
        // Check if Galleys should be auto-calculated (Galleys selected with land troops)
        const galleyCheckbox = this.container.querySelector('.unit-checkbox[data-unit-name="Galley"]');
        const hasGalleys = galleyCheckbox && galleyCheckbox.checked;
        const galleyInEligible = eligibleUnits.includes('Galley');
        const autoCalculateGalleys = hasGalleys && galleyInEligible;
        
        // Separate land troops from Galleys if auto-calculating
        const landTroopUnits = autoCalculateGalleys ? eligibleUnits.filter(name => name !== 'Galley') : eligibleUnits;
        
        // Generate percentage inputs (only for land troops if auto-calculating Galleys)
        let totalPercentage = 0;
        const inputsHTML = landTroopUnits.map(unitName => {
            const count = this.selectedUnits.get(unitName) || 0;
            const unit = UNITS.find(u => u.name === unitName);
            if (!unit) return '';
            
            const unitCapacity = unit.space * count;
            const percentage = totalEligibleCapacity > 0 ? (unitCapacity / totalEligibleCapacity * 100) : defaultPercentage;
            totalPercentage += percentage;
            
            return `
                <div class="percentage-input-row">
                    <label class="percentage-label">${unitName}:</label>
                    <input type="number" 
                           class="percentage-input" 
                           data-unit-name="${unitName}"
                           min="0" 
                           max="100" 
                           step="0.1"
                           value="${percentage.toFixed(1)}">
                    <span class="percentage-symbol">%</span>
                </div>
            `;
        }).join('');
        
        // Add Galley display (read-only) if auto-calculating
        let galleyDisplayHTML = '';
        if (autoCalculateGalleys) {
            const galleyCount = this.selectedUnits.get('Galley') || 0;
            const galleyUnit = UNITS.find(u => u.name === 'Galley');
            const galleyCapacity = galleyUnit ? galleyUnit.space * galleyCount : 0;
            const galleyPercentage = totalEligibleCapacity > 0 ? (galleyCapacity / totalEligibleCapacity * 100) : 0;
            totalPercentage += galleyPercentage;
            
            galleyDisplayHTML = `
                <div class="percentage-input-row">
                    <label class="percentage-label">Galley (auto):</label>
                    <span class="percentage-display">${galleyPercentage.toFixed(1)}%</span>
                    <span class="percentage-symbol">%</span>
                </div>
            `;
        }
        
        const finalInputsHTML = inputsHTML + galleyDisplayHTML;
        
        inputsContainer.innerHTML = finalInputsHTML;
        
        // Update total
        totalValueEl.textContent = `${totalPercentage.toFixed(1)}%`;
        if (Math.abs(totalPercentage - 100) < 0.1) {
            totalValueEl.style.color = 'var(--success-color, #4caf50)';
        } else {
            totalValueEl.style.color = 'var(--error-color, #f44336)';
        }
        
        // Add event listeners to percentage inputs
        const percentageInputs = inputsContainer.querySelectorAll('.percentage-input');
        percentageInputs.forEach(input => {
            input.addEventListener('input', (e) => {
                this.handlePercentageInputChange(e.target);
            });
        });
    }
    
    /**
     * Handle individual percentage input change with 100% cap
     */
    handlePercentageInputChange(changedInput) {
        const eligibleUnits = this.getEligibleUnitsForPercentage();
        if (eligibleUnits.length < 2) return;
        
        // Get all percentage inputs
        const allInputs = Array.from(this.container.querySelectorAll('.percentage-input'));
        
        // Calculate current total (excluding the changed input)
        let currentTotal = 0;
        allInputs.forEach(input => {
            if (input !== changedInput) {
                currentTotal += parseFloat(input.value) || 0;
            }
        });
        
        // Get the new value from the changed input
        let newValue = parseFloat(changedInput.value) || 0;
        
        // Cap the new value so total doesn't exceed 100%
        const maxAllowed = 100 - currentTotal;
        if (newValue > maxAllowed) {
            newValue = Math.max(0, maxAllowed);
            changedInput.value = newValue.toFixed(1);
        }
        
        // Ensure value is not negative
        if (newValue < 0) {
            newValue = 0;
            changedInput.value = '0.0';
        }
        
        // Now handle the percentage change
        this.handlePercentageChange();
    }
    
    /**
     * Handle percentage input changes and redistribute troops
     */
    handlePercentageChange() {
        const eligibleUnits = this.getEligibleUnitsForPercentage();
        if (eligibleUnits.length < 2) return;
        
        // Prevent infinite loops
        if (this._updatingPercentages) return;
        
        // Get percentage values from inputs
        const percentages = new Map();
        let totalPercentage = 0;
        
        eligibleUnits.forEach(unitName => {
            const input = this.container.querySelector(`.percentage-input[data-unit-name="${unitName}"]`);
            if (input) {
                const percentage = parseFloat(input.value) || 0;
                percentages.set(unitName, percentage);
                totalPercentage += percentage;
            }
        });
        
        // Update total display
        const totalValueEl = this.container.querySelector('#percentage-total-value');
        if (totalValueEl) {
            totalValueEl.textContent = `${totalPercentage.toFixed(1)}%`;
            if (Math.abs(totalPercentage - 100) < 0.1) {
                totalValueEl.style.color = 'var(--success-color, #4caf50)';
            } else {
                totalValueEl.style.color = 'var(--error-color, #f44336)';
            }
        }
        
        // If total is not 100%, don't redistribute yet
        if (Math.abs(totalPercentage - 100) > 0.1) {
            return;
        }
        
        // Check if Galleys are needed
        const galleyCheckbox = this.container.querySelector('.unit-checkbox[data-unit-name="Galley"]');
        const hasGalleys = galleyCheckbox && galleyCheckbox.checked;
        const galleyInEligible = eligibleUnits.includes('Galley');
        
        // Calculate available capacity for eligible units
        // This is the total capacity minus capacity used by non-eligible units
        const totalCapacity = this.gameEngine.troop_capacity_tot;
        let nonEligibleUsed = 0;
        this.selectedUnits.forEach((count, unitName) => {
            if (!eligibleUnits.includes(unitName)) {
                const unit = UNITS.find(u => u.name === unitName);
                if (unit) {
                    nonEligibleUsed += unit.space * count;
                }
            }
        });
        
        // Available capacity for eligible units
        let availableCapacity = Math.max(0, totalCapacity - nonEligibleUsed);
        
        // For Galleys: automatically calculate required Galleys based on land troop capacity
        if (hasGalleys && galleyInEligible) {
            // Separate land troops from Galleys (Galleys are auto-calculated, not from percentage input)
            const landTroopUnits = eligibleUnits.filter(name => name !== 'Galley');
            
            // Allocate land troops based on their percentages (should total 100% for land troops)
            let landTroopCapacity = 0;
            const landTroopCounts = new Map();
            
            // Calculate land troop capacity from percentages
            // The percentages should total 100% for land troops only
            let landTroopPercentTotal = 0;
            landTroopUnits.forEach(unitName => {
                const percentage = percentages.get(unitName) || 0;
                landTroopPercentTotal += percentage;
            });
            
            // If percentages don't total 100%, normalize them
            if (landTroopPercentTotal > 0 && Math.abs(landTroopPercentTotal - 100) > 0.1) {
                landTroopUnits.forEach(unitName => {
                    const percentage = percentages.get(unitName) || 0;
                    percentages.set(unitName, (percentage / landTroopPercentTotal) * 100);
                });
            }
            
            // Solve: landTroopCapacity + Math.ceil(landTroopCapacity / 500) * 100 = availableCapacity
            // Use binary search approach to find maximum land troop capacity more precisely
            const galleyTransportCapacity = this.getTransportCapacity('Galley'); // 500 per Galley
            const galleyUnit = UNITS.find(u => u.name === 'Galley');
            const galleySpace = galleyUnit?.space || 100;
            
            // Binary search for optimal land troop capacity
            let low = 0;
            let high = availableCapacity;
            let bestLandTroopCapacity = 0;
            
            while (low <= high) {
                const mid = Math.floor((low + high) / 2);
                const testRequiredGalleys = Math.ceil(mid / galleyTransportCapacity);
                const testRequiredGalleyCapacity = testRequiredGalleys * galleySpace;
                const testTotal = mid + testRequiredGalleyCapacity;
                
                if (testTotal <= availableCapacity) {
                    bestLandTroopCapacity = mid;
                    low = mid + 1; // Try to find a larger value
                } else {
                    high = mid - 1; // Too large, reduce
                }
            }
            
            // Fine-tune: check values around the found maximum to ensure we get the absolute maximum
            let bestCapacity = bestLandTroopCapacity;
            let bestTotal = bestLandTroopCapacity + Math.ceil(bestLandTroopCapacity / galleyTransportCapacity) * galleySpace;
            
            // Check values around the found maximum with fine-grained steps
            for (let offset = -200; offset <= 200; offset += 1) {
                const testCapacity = bestLandTroopCapacity + offset;
                if (testCapacity < 0 || testCapacity > availableCapacity) continue;
                
                const testRequiredGalleys = Math.ceil(testCapacity / galleyTransportCapacity);
                const testRequiredGalleyCapacity = testRequiredGalleys * galleySpace;
                const testTotal = testCapacity + testRequiredGalleyCapacity;
                
                if (testTotal <= availableCapacity && testTotal > bestTotal) {
                    bestCapacity = testCapacity;
                    bestTotal = testTotal;
                }
            }
            
            bestLandTroopCapacity = bestCapacity;
            
            // Allocate land troops based on percentages, using bestLandTroopCapacity
            // We'll allocate to maximize capacity usage
            let allocatedCapacity = 0;
            
            // Allocate land troops (all but last)
            for (let i = 0; i < landTroopUnits.length - 1; i++) {
                const unitName = landTroopUnits[i];
                const unit = UNITS.find(u => u.name === unitName);
                if (!unit || unit.space === 0) continue;
                
                const unitPercentage = percentages.get(unitName) || 0;
                const targetCapacity = (bestLandTroopCapacity * (unitPercentage / 100));
                const newCount = Math.floor(targetCapacity / unit.space);
                const usedCapacity = newCount * unit.space;
                
                landTroopCounts.set(unitName, newCount);
                allocatedCapacity += usedCapacity;
            }
            
            // Last land troop unit gets remaining capacity to maximize usage
            const lastLandUnitName = landTroopUnits[landTroopUnits.length - 1];
            const lastLandUnit = UNITS.find(u => u.name === lastLandUnitName);
            if (lastLandUnit && lastLandUnit.space > 0) {
                const remainingForLast = bestLandTroopCapacity - allocatedCapacity;
                const newCount = Math.floor(remainingForLast / lastLandUnit.space);
                landTroopCounts.set(lastLandUnitName, newCount);
                allocatedCapacity += newCount * lastLandUnit.space;
            }
            
            // Calculate required Galleys based on actual allocated land troop capacity
            const requiredGalleys = Math.ceil(allocatedCapacity / galleyTransportCapacity);
            landTroopCounts.set('Galley', requiredGalleys);
            
            // Calculate actual total capacity used
            const actualGalleyCapacity = requiredGalleys * galleySpace;
            const actualTotalCapacity = allocatedCapacity + actualGalleyCapacity;
            
            // If we have remaining capacity and it's significant, try to add more to the last unit
            const remainingCapacity = availableCapacity - actualTotalCapacity;
            if (remainingCapacity > 0 && lastLandUnit && lastLandUnit.space > 0) {
                const additionalUnits = Math.floor(remainingCapacity / lastLandUnit.space);
                if (additionalUnits > 0) {
                    // Check if adding these units would require more Galleys
                    const newLandCapacity = allocatedCapacity + (additionalUnits * lastLandUnit.space);
                    const newRequiredGalleys = Math.ceil(newLandCapacity / galleyTransportCapacity);
                    const newGalleyCapacity = newRequiredGalleys * galleySpace;
                    const newTotal = newLandCapacity + newGalleyCapacity;
                    
                    // Only add if it still fits
                    if (newTotal <= availableCapacity) {
                        const currentLastCount = landTroopCounts.get(lastLandUnitName) || 0;
                        landTroopCounts.set(lastLandUnitName, currentLastCount + additionalUnits);
                        landTroopCounts.set('Galley', newRequiredGalleys);
                    }
                }
            }
            
            // Update selected units
            eligibleUnits.forEach(unitName => {
                const newCount = landTroopCounts.get(unitName) || 0;
                this.selectedUnits.set(unitName, newCount);
                this.updateUnitDisplay(unitName, newCount);
                
                // Update slider
                const slider = this.container.querySelector(`.unit-slider[data-unit-name="${unitName}"]`);
                if (slider) {
                    slider.value = newCount;
                }
                
                // Update input field
                const input = this.container.querySelector(`.unit-count-input[data-unit-name="${unitName}"]`);
                if (input) {
                    input.value = newCount;
                }
            });
            
            // Set flag to prevent percentage update during summary update
            this._updatingPercentages = true;
            
            // Update slider max values and summary
            this.updateSliderMaxValues();
            // Note: updateAutomaticGalleys is not needed here since we just calculated Galleys manually
            this.updateSummary();
            
            // Clear flag after update
            this._updatingPercentages = false;
            return;
        }
        
        // Standard redistribution (non-water city or no Galleys needed)
        // Redistribute troops based on percentages
        // Calculate new capacity for each unit
        const newCounts = new Map();
        let remainingCapacity = availableCapacity;
        
        // Distribute to all but the last unit
        for (let i = 0; i < eligibleUnits.length - 1; i++) {
            const unitName = eligibleUnits[i];
            const unit = UNITS.find(u => u.name === unitName);
            if (!unit || unit.space === 0) continue;
            
            const percentage = percentages.get(unitName) || 0;
            const targetCapacity = (availableCapacity * percentage) / 100;
            const newCount = Math.floor(targetCapacity / unit.space);
            const usedCapacity = newCount * unit.space;
            
            newCounts.set(unitName, newCount);
            remainingCapacity -= usedCapacity;
        }
        
        // Last unit gets the remaining capacity
        const lastUnitName = eligibleUnits[eligibleUnits.length - 1];
        const lastUnit = UNITS.find(u => u.name === lastUnitName);
        if (lastUnit && lastUnit.space > 0) {
            const newCount = Math.floor(remainingCapacity / lastUnit.space);
            newCounts.set(lastUnitName, newCount);
        }
        
        // Update selected units
        eligibleUnits.forEach(unitName => {
            const newCount = newCounts.get(unitName) || 0;
            this.selectedUnits.set(unitName, newCount);
            this.updateUnitDisplay(unitName, newCount);
            
            // Update slider
            const slider = this.container.querySelector(`.unit-slider[data-unit-name="${unitName}"]`);
            if (slider) {
                slider.value = newCount;
            }
            
            // Update input field
            const input = this.container.querySelector(`.unit-count-input[data-unit-name="${unitName}"]`);
            if (input) {
                input.value = newCount;
            }
            
            // Don't uncheck checkbox - preserve the selection state
            // The checkbox should remain checked if it was checked for percentage allocation
        });
        
        // Set flag to prevent percentage update during summary update
        this._updatingPercentages = true;
        
        // Update slider max values and summary
        this.updateSliderMaxValues();
        this.updateSummary();
        
        // Clear flag after update
        this._updatingPercentages = false;
    }
    
    /**
     * Format individual build time (for single unit)
     * For times >= 1 minute but < 1 hour: use xM SS.MS format
     * For times >= 1 hour: use xH xM SS.MS format
     */
    formatBuildTime(seconds) {
        if (seconds < 60) {
            // Round to 2 decimal places for times under 1 minute
            return seconds > 0 ? (Math.round(seconds * 100) / 100).toFixed(2) + 's' : '0.00s';
        } else if (seconds < 3600) {
            // For times >= 1 minute but < 1 hour: use xM SS.MS format
            const minutes = Math.floor(seconds / 60);
            const remainingSeconds = seconds % 60;
            // Round to 2 decimal places for seconds with milliseconds
            const secsWithMs = (Math.round(remainingSeconds * 100) / 100).toFixed(2);
            return `${minutes}M ${secsWithMs}`;
        } else {
            // For times >= 1 hour: use xH xM SS.MS format
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            const remainingSeconds = seconds % 60;
            // Round to 2 decimal places for seconds with milliseconds
            const secsWithMs = (Math.round(remainingSeconds * 100) / 100).toFixed(2);
            return `${hours}H ${minutes}M ${secsWithMs}`;
        }
    }
    
    /**
     * Format duration in seconds to readable format (for full build times)
     * Uses xD xH xM xS format
     */
    formatDuration(seconds) {
        if (seconds < 60) {
            return `${Math.round(seconds)}s`;
        } else if (seconds < 3600) {
            const minutes = Math.floor(seconds / 60);
            const secs = Math.round(seconds % 60);
            return `${minutes}m ${secs}s`;
        } else if (seconds < 86400) {
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            return `${hours}h ${minutes}m`;
        } else {
            const days = Math.floor(seconds / 86400);
            const hours = Math.floor((seconds % 86400) / 3600);
            return `${days}d ${hours}h`;
        }
    }
    
    /**
     * Format number with commas
     */
    formatNumber(num) {
        return Math.round(num).toLocaleString();
    }
    
    /**
     * Format unit cost with resource icons
     */
    formatCost(unit) {
        const parts = [];
        const iconSize = '16px';
        const iconStyle = `width: ${iconSize}; height: ${iconSize}; vertical-align: middle; margin: 0 2px;`;
        
        if (unit.wood > 0) {
            parts.push(`<img src="./assets/images/ui/icon_resource_wood.png" alt="Wood" style="${iconStyle}" onerror="this.style.display='none'">${this.formatNumber(unit.wood)}`);
        }
        if (unit.stone > 0) {
            parts.push(`<img src="./assets/images/ui/icon_resource_stone.png" alt="Stone" style="${iconStyle}" onerror="this.style.display='none'">${this.formatNumber(unit.stone)}`);
        }
        if (unit.iron > 0) {
            parts.push(`<img src="./assets/images/ui/icon_resource_iron.png" alt="Iron" style="${iconStyle}" onerror="this.style.display='none'">${this.formatNumber(unit.iron)}`);
        }
        if (unit.gold > 0) {
            parts.push(`<img src="./assets/images/ui/icon_resource_gold.png" alt="Gold" style="${iconStyle}" onerror="this.style.display='none'">${this.formatNumber(unit.gold)}`);
        }
        
        return parts.length > 0 ? parts.join(' ') : 'Free';
    }
    
    /**
     * Get troop numbers as a JSON array in the correct order (layoutTroops format)
     * Returns an array of 18 numbers, where each index corresponds to a unit type:
     * 0 - Guard, 1 - Ballista, 2 - Ranger, 3 - Triari, 4 - Priestess,
     * 5 - Vanquisher, 6 - Sorceres, 7 - Scout, 8 - Arbalist, 9 - Praetor,
     * 10 - Horseman, 11 - Druid, 12 - Battering Ram, 13 - Scorpion,
     * 14 - Galley, 15 - Stinger, 16 - Warship, 17 - Senator
     * @returns {number[]} Array of troop counts in layoutTroops format
     */
    getTroopNumbersArray() {
        // Create array with 18 zeros (one for each unit type)
        const troopArray = new Array(18).fill(0);
        
        // Map unit names to their indices in layoutTroops format
        const unitNameToIndex = {
            'Guard': 0,
            'Ballista': 1,
            'Ranger': 2,
            'Triari': 3,
            'Priestess': 4,
            'Vanquisher': 5,
            'Sorceres': 6,
            'Scout': 7,
            'Arbalist': 8,
            'Praetor': 9,
            'Horseman': 10,
            'Druid': 11,
            'Battering Ram': 12,
            'Scorpion': 13,
            'Galley': 14,
            'Stinger': 15,
            'Warship': 16,
            'Senator': 17
        };
        
        // Map unit names to their indices in the layoutTroops format
        this.selectedUnits.forEach((count, unitName) => {
            const arrayIndex = unitNameToIndex[unitName];
            if (arrayIndex !== undefined && arrayIndex >= 0 && arrayIndex < 18 && count > 0) {
                troopArray[arrayIndex] = count;
            }
        });
        
        return troopArray;
    }
    
    /**
     * Set troop numbers from an array
     * Maps array indices to unit names based on layoutTroops format:
     * 0 - Guard, 1 - Ballista, 2 - Ranger, 3 - Triari, 4 - Priestess,
     * 5 - Vanquisher, 6 - Sorceres, 7 - Scout, 8 - Arbalist, 9 - Praetor,
     * 10 - Horseman, 11 - Druid, 12 - Battering Ram, 13 - Scorpion,
     * 14 - Galley, 15 - Stinger, 16 - Warship, 17 - Senator
     * Handles both 17-element arrays (missing Guard) and 18-element arrays
     * Automatically checks checkboxes and shows controls for units with non-zero counts
     * @param {number[]} troopArray - Array of troop counts in layoutTroops format
     */
    setTroopNumbersArray(troopArray) {
        if (!Array.isArray(troopArray)) {
            console.warn('setTroopNumbersArray: Expected array, got', typeof troopArray);
            return;
        }
        
        // Clear current selections
        this.selectedUnits.clear();
        
        // Map array indices to unit names (layoutTroops format)
        const indexToUnitName = {
            0: 'Guard',
            1: 'Ballista',
            2: 'Ranger',
            3: 'Triari',
            4: 'Priestess',
            5: 'Vanquisher',
            6: 'Sorceres',
            7: 'Scout',
            8: 'Arbalist',
            9: 'Praetor',
            10: 'Horseman',
            11: 'Druid',
            12: 'Battering Ram',
            13: 'Scorpion',
            14: 'Galley',
            15: 'Stinger',
            16: 'Warship',
            17: 'Senator'
        };
        
        // Handle arrays - 17 elements means missing Senator (index 17), 18 elements is complete
        // Both formats start from index 0 (Guard)
        const maxIndex = Math.min(troopArray.length, 18);
        
        // Set troop counts for each unit using the correct mapping
        // Array index directly maps to layoutTroops format index
        for (let i = 0; i < maxIndex; i++) {
            const count = troopArray[i];
            
            if (count > 0 && i < 18) {
                const unitName = indexToUnitName[i];
                if (unitName) {
                    this.selectedUnits.set(unitName, count);
                }
            }
        }
        
        // Update the UI to reflect the new selections
        // This will check boxes, show controls, and set slider/input values
        this.update();
        
        // After update, ensure checkboxes are checked and controls are visible
        this.selectedUnits.forEach((count, unitName) => {
            if (count > 0) {
                const checkbox = this.container.querySelector(`.unit-checkbox[data-unit-name="${unitName}"]`);
                if (checkbox) {
                    checkbox.checked = true;
                    const unitCard = checkbox.closest('.unit-card');
                    const controls = unitCard?.querySelector('.unit-controls');
                    if (controls) {
                        controls.style.display = 'block';
                    }
                    
                    // Update slider value
                    const slider = unitCard?.querySelector('.unit-slider');
                    if (slider) {
                        slider.value = count;
                    }
                    
                    // Update input field
                    const input = unitCard?.querySelector('.unit-count-input');
                    if (input) {
                        input.value = count;
                    }
                }
            }
        });
        
        // Update slider max values and summary
        this.updateSliderMaxValues();
        this.updateSummary();
    }
    
    /**
     * Update the panel when game engine state changes
     */
    update() {
        // Preserve selected units before re-rendering
        const preservedSelections = new Map(this.selectedUnits);
        this.render();
        // Restore selections after render
        this.selectedUnits = preservedSelections;
        // Update checkboxes and sliders to match preserved selections
        preservedSelections.forEach((count, unitName) => {
            const checkbox = this.container.querySelector(`.unit-checkbox[data-unit-name="${unitName}"]`);
            if (checkbox) {
                checkbox.checked = count > 0;
                const unitCard = checkbox.closest('.unit-card');
                const controls = unitCard.querySelector('.unit-controls');
                if (controls) {
                    controls.style.display = count > 0 ? 'block' : 'none';
                }
                const slider = unitCard.querySelector('.unit-slider');
                if (slider) {
                    slider.value = count;
                }
                this.updateUnitDisplay(unitName, count);
            }
        });
        // Update slider max values after restoring selections
        this.updateSliderMaxValues();
        this.updateSummary();
    }
}

